import { BirdBowlData } from "../../types/bird/bird-bowl-data";
import { getMetricValue, humanReadableMetric } from "./insights-page-utils";

interface Props {
  readonly latest: ReadonlyArray<BirdBowlData>;
  readonly historic: ReadonlyArray<BirdBowlData>;
  readonly overRange: { start: number; end: number };
  readonly selectedInnings: "both" | "first" | "second";
  readonly applyFilters: (
    data: ReadonlyArray<BirdBowlData>,
    overRange: { start: number; end: number },
    innings: "both" | "first" | "second"
  ) => BirdBowlData[];
}

export default function MetricComparisonTable({
  latest,
  historic,
  overRange,
  selectedInnings,
  applyFilters,
}: Props) {
  return (
    <table
      className="insights-metrics-table"
      style={{
        width: "100%",
        borderCollapse: "collapse",
        marginBottom: "1rem",
      }}
    >
      <thead>
        <tr>
          <th style={thStyle}>Metric</th>
          <th style={thStyle}>Mean Diff</th>
          <th style={thStyle}>Std Dev Diff</th>
        </tr>
      </thead>
      <tbody>
        {["bounceHeight", "slowDown", "deviation", "swing"].map((metric) => {
          const scopedLatest = applyFilters(latest, overRange, selectedInnings);
          const scopedHistoric = applyFilters(historic, overRange, selectedInnings);

          const todayValues = scopedLatest.map((u) => getMetricValue(u, metric));
          const historicValues = scopedHistoric.map((u) => getMetricValue(u, metric));

          const todayMean = mean(todayValues);
          const historicMean = mean(historicValues);
          const meanDiffPercent = ((todayMean - historicMean) / historicMean) * 100;
          const meanDirection = todayMean > historicMean ? "higher" : "lower";

          const todayStd = stdDev(todayValues);
          const historicStd = stdDev(historicValues);
          const stdDevDiffPercent = ((todayStd - historicStd) / historicStd) * 100;
          const stdDevDirection = todayStd > historicStd ? "higher" : "lower";

          const meanColor = getColor(todayValues, historicValues, meanDiffPercent, meanDirection);
          const stdDevColor = getColor(todayValues, historicValues, stdDevDiffPercent, stdDevDirection);

          const meanArrow = meanDirection === "higher" ? "▲" : "▼";
          const stdDevArrow = stdDevDirection === "higher" ? "▲" : "▼";

          const meanDiffText = todayValues.length === 0 || historicValues.length === 0
            ? ""
            : `${meanArrow}${meanDiffPercent.toFixed(0)}%`;

          const stdDevDiffText = todayValues.length === 0 || historicValues.length === 0
            ? ""
            : `${stdDevArrow}${Math.abs(stdDevDiffPercent).toFixed(0)}%`;

          return (
            <tr key={metric}>
              <td style={tdStyle}>{humanReadableMetric[metric]}</td>
              <td style={{ ...tdStyle, color: meanColor }}>{meanDiffText}</td>
              <td style={{ ...tdStyle, color: stdDevColor }}>{stdDevDiffText}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

const mean = (arr: number[]) =>
  arr.reduce((a, b) => a + b, 0) / (arr.length || 1);

const stdDev = (arr: number[]) => {
  const m = mean(arr);
  const variance = arr.reduce((a, b) => a + Math.pow(b - m, 2), 0) / (arr.length || 1);
  return Math.sqrt(variance);
};

const getColor = (
  todayValues: number[],
  historicValues: number[],
  diffPercent: number,
  direction: "higher" | "lower"
) => {
  const capped = Math.min(Math.abs(diffPercent), 50);
  const saturation = (capped / 50) * 100;
  if (todayValues.length === 0 || historicValues.length === 0) return "#444";
  return direction === "higher"
    ? `hsl(120, ${saturation}%, 35%)`
    : `hsl(0, ${saturation}%, 45%)`;
};

const thStyle = {
  textAlign: "left" as const,
  borderBottom: "1px solid #ccc",
  padding: "0.5rem",
};

const tdStyle = {
  padding: "0.5rem",
  borderBottom: "1px solid #eee",
};